import { createContext, useContext, useState, ReactNode } from 'react';
import type { Language, Translations, LanguageInfo } from './types';
import { LANGUAGES, DEFAULT_LANGUAGE } from './types';

// Import translations
import { ko } from './translations/ko';
import { en } from './translations/en';
import { ja } from './translations/ja';
import { zhCN } from './translations/zh-CN';
import { zhTW } from './translations/zh-TW';

// Translation map
const translations: Record<Language, Translations> = {
    ko,
    en,
    ja,
    'zh-CN': zhCN,
    'zh-TW': zhTW,
};

// Storage key
const LANGUAGE_STORAGE_KEY = 'app_language';

// Get saved language or browser default
function getInitialLanguage(): Language {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && saved in translations) {
        return saved as Language;
    }

    // Try browser language
    const browserLang = navigator.language;
    if (browserLang.startsWith('ko')) return 'ko';
    if (browserLang.startsWith('ja')) return 'ja';
    if (browserLang === 'zh-CN' || browserLang === 'zh-Hans') return 'zh-CN';
    if (browserLang === 'zh-TW' || browserLang === 'zh-Hant') return 'zh-TW';
    if (browserLang.startsWith('zh')) return 'zh-CN';
    if (browserLang.startsWith('en')) return 'en';

    return DEFAULT_LANGUAGE;
}

// Context type
interface I18nContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: Translations;
    languages: LanguageInfo[];
    formatDate: (date: Date | string, style?: 'full' | 'long' | 'medium' | 'short') => string;
    formatTime: (date: Date | string) => string;
    formatRelativeTime: (date: Date | string, showActualTime?: boolean) => string;
    tf: (key: string) => string;
}

// Create context
const I18nContext = createContext<I18nContextType | null>(null);

// Provider component
export function I18nProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(getInitialLanguage);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    };

    const t = translations[language];
    const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

    // Helper to get translation by string key path (e.g. 'settings.apiKey')
    const tf = (key: string): string => {
        const keys = key.split('.');
        let value: any = t;
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k as keyof typeof value];
            } else {
                return key;
            }
        }
        return typeof value === 'string' ? value : key;
    };

    // Format date according to current language
    const formatDate = (date: Date | string, style: 'full' | 'long' | 'medium' | 'short' = 'medium') => {
        const d = typeof date === 'string' ? new Date(date) : date;
        return new Intl.DateTimeFormat(currentLang.locale, {
            dateStyle: style,
        }).format(d);
    };

    // Format time according to current language
    const formatTime = (date: Date | string) => {
        const d = typeof date === 'string' ? new Date(date) : date;
        return new Intl.DateTimeFormat(currentLang.locale, {
            timeStyle: 'short',
        }).format(d);
    };

    // Format relative time with actual time (e.g., "5분 전 (14:30)")
    const formatRelativeTime = (date: Date | string, showActualTime = true) => {
        let d: Date;
        if (typeof date === 'string') {
            // DB returns time without timezone info (e.g., "2026-01-16 14:27:06")
            // Treat it as UTC by appending Z if no timezone info
            const dateStr = date.includes('T') ? date : date.replace(' ', 'T');
            d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        } else {
            d = date;
        }

        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        let relative: string;
        if (diffMins < 1) relative = t.time.justNow;
        else if (diffMins < 60) relative = `${diffMins}${t.time.minutesAgo}`;
        else if (diffHours < 24) relative = `${diffHours}${t.time.hoursAgo}`;
        else relative = `${diffDays}${t.time.daysAgo}`;

        if (showActualTime) {
            const timeStr = new Intl.DateTimeFormat(currentLang.locale, {
                hour: '2-digit',
                minute: '2-digit',
            }).format(d);
            return `${relative} (${timeStr})`;
        }

        return relative;
    };

    const value: I18nContextType = {
        language,
        setLanguage,
        t,
        tf,
        languages: LANGUAGES,
        formatDate,
        formatTime,
        formatRelativeTime,
    };

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
}

// Hook to use i18n
export function useI18n() {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
}

// Re-export types
export type { Language, Translations, LanguageInfo } from './types';
export { LANGUAGES, DEFAULT_LANGUAGE } from './types';
